// Finding duplicate athlete records.
//
// Two records are the same person when EITHER relation holds:
//
//   1. Same athleteType, and that type's own key matches. Adults group by name
//      alone (catches same name / different email); juniors also require parent
//      and birthday, so two unrelated kids who share a name never group —
//      Andy's daughters Andrea and Gabriela stay separate.
//
//   2. Same name, birthdays within a day. This is the cross-type case, and it
//      used to be invisible: the key was type-prefixed, so an archived
//      PushPress import typed 'adult' could never meet the live junior it
//      duplicated. 13 sat unnoticed that way. The one-day tolerance is not
//      slack — that import shifted some dates by a timezone, so a sibling's
//      record reads 2012-03-03 against 2012-03-04 on the live one.
//
// Union-find, because both relations can fire on the same record and the groups
// must not overlap: a record shown in two groups could be merged twice, the
// second merge acting on a doc the first already deleted.

const norm = (v) => (v || '').toLowerCase().trim();

/** Days since epoch for a YYYY-MM-DD string, or null if it isn't one. */
const dayNumber = (b) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((b || '').trim());
  return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) / 86400000 : null;
};

/** Only adults and juniors merge; parents and unknown types are excluded. */
const isCandidate = (a) => {
  const t = norm(a.athleteType);
  if (t !== 'junior' && t !== 'adult' && t !== 'both') return false;
  return !!(norm(a.firstName) || norm(a.lastName)); // no name at all = nothing to match
};

const sameTypeKey = (a) => {
  const fn = norm(a.firstName);
  const ln = norm(a.lastName);
  return norm(a.athleteType) === 'junior'
    ? `j|${fn}|${ln}|${a.parentUserId || ''}|${a.birthday || ''}`
    : `a|${fn}|${ln}`;
};

// Adults => same email when same person; juniors => same parent + birthday.
const strictDisc = (a) => (
  norm(a.athleteType) === 'junior'
    ? `j|${a.parentUserId || ''}|${a.birthday || ''}`
    : `a|${norm(a.email)}`
);

/**
 * Group athlete records that look like the same person.
 *
 * Returns [{ key, kind, reason, docs }] for every group of two or more, where
 * kind is 'likely' or 'possible' and reason is the label to show.
 */
export function groupDuplicateAthletes(all) {
  const candidates = (all || []).filter(isCandidate);
  const uf = candidates.map((_, i) => i);
  const find = (i) => { while (uf[i] !== i) { uf[i] = uf[uf[i]]; i = uf[i]; } return i; };
  const union = (i, j) => { const a = find(i), b = find(j); if (a !== b) uf[b] = a; };

  // Relation 1
  const seen = new Map();
  candidates.forEach((a, i) => {
    const k = sameTypeKey(a);
    if (seen.has(k)) union(seen.get(k), i);
    else seen.set(k, i);
  });

  // Relation 2. A record with no birthday links to nothing here: without one
  // the only thing left is the name, and grouping on that alone across types
  // would flag a father and son as duplicates.
  const byName = new Map();
  candidates.forEach((a, i) => {
    const n = `${norm(a.firstName)}|${norm(a.lastName)}`;
    if (!byName.has(n)) byName.set(n, []);
    byName.get(n).push(i);
  });
  for (const idxs of byName.values()) {
    if (idxs.length < 2) continue;
    for (let x = 0; x < idxs.length; x++) {
      for (let y = x + 1; y < idxs.length; y++) {
        const d1 = dayNumber(candidates[idxs[x]].birthday);
        const d2 = dayNumber(candidates[idxs[y]].birthday);
        if (d1 !== null && d2 !== null && Math.abs(d1 - d2) <= 1) union(idxs[x], idxs[y]);
      }
    }
  }

  const groups = new Map();
  candidates.forEach((a, i) => {
    const root = find(i);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root).push(a);
  });

  // A shared birthday is stronger evidence than a shared email, so a group that
  // agrees on one is 'likely' even when the other disagrees.
  const sharesBirthday = (docs) => {
    const days = docs.map(d => dayNumber(d.birthday));
    if (days.some(d => d === null)) return false;
    return Math.max(...days) - Math.min(...days) <= 1;
  };
  const crossType = (docs) => new Set(docs.map(d => norm(d.athleteType))).size > 1;

  return [...groups.values()]
    .filter(docs => docs.length > 1)
    .map(docs => {
      const likely = sharesBirthday(docs) || new Set(docs.map(strictDisc)).size === 1;
      return {
        key: `grp|${docs.map(d => d.id).sort()[0]}`,
        kind: likely ? 'likely' : 'possible',
        reason: crossType(docs)
          ? 'Same name and birthday, different record types'
          : likely ? 'Likely duplicate' : 'Possible - same name, different email',
        docs,
      };
    });
}
